import type { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import React, {
  type Dispatch,
  type SetStateAction,
  useState,
  useEffect,
  useRef,
  useCallback,
  memo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { PSAPInfo } from '@rsos/reduxDataStore/src/features/capstone/psapInfo/psapInfoSlice';
import { useCallQueueFilters } from '@rsos/reduxDataStore/src/features/selectors/callQueueSelector';
import { updateUserAttributes } from '@rsos/sinatra/src/actions/updateAttributes';
import { captureException } from '@rsos/utils/sentry';
import { notifyError } from '@rsos/utils/toastUtils';
import useIsIRP from '@rsos/utils/useIsIRP';
import type { RootState } from '../../../app/rootReducer';
import useCallTakerModePerms from '../../../hooks/useCallTakerModePerms';
import { ctmDisableErrorMsg } from '../constants';
import { trackCTMEnabled } from '../ctmTracking';
import ErrorModal from './ErrorModal';
import InitiationModal from './InitiationModal';
import PositionIDSelectionModal from './PositionIDSelectionModal';

interface CallTakerModeSettings {
  auto_switch_to_next_call: boolean;
  is_enabled: boolean;
  remember_workstation_position: boolean;
  workstation_position: string;
}

interface ApplicationAttributes {
  call_taker_mode_settings?: CallTakerModeSettings;
}

interface CallTakerModePSAPInfo extends PSAPInfo {
  workstation_positions?: string[];
}

interface CallTakerModalsContainerProps {
  isCTMClicked: boolean;
  setIsCTMClicked: Dispatch<SetStateAction<boolean>>;
}

type EmergencyDataDispatch = ThunkDispatch<RootState, unknown, UnknownAction>;

const arePropsEqual = (
  prevProps: CallTakerModalsContainerProps,
  nextProps: CallTakerModalsContainerProps,
): boolean => {
  return (
    prevProps.isCTMClicked === nextProps.isCTMClicked && !nextProps.isCTMClicked
  );
};

const CallTakerModalsContainerComponent = ({
  isCTMClicked,
  setIsCTMClicked,
}: CallTakerModalsContainerProps): JSX.Element => {
  const dispatch = useDispatch() as unknown as EmergencyDataDispatch;
  const [isInitiationModalOpen, setIsInitiationModalOpen] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [isPositionIDSelectionModalOpen, setIsPositionIDSelectionModalOpen] =
    useState(false);
  // Prevent the login-time prompt from rerunning after user attributes update.
  const hasHandledInitialAutoDisplayRef = useRef(false);
  const ctmPermission = useCallTakerModePerms();
  const { toggleSetCallFilter } = useCallQueueFilters();
  const { irpVersion } = useIsIRP();
  const { workstation_positions = [], activeCapabilities = {} } = useSelector(
    (state: RootState) => state.psaps.currentPSAP as CallTakerModePSAPInfo,
  );

  const { user } = useSelector((state: RootState) => state.sinatra);
  const applicationName = user.currentRole?.application ?? '';
  const applicationAttributes = user.profile?.attributes?.[applicationName] as
    | ApplicationAttributes
    | undefined;
  const callTakerModeSettings = applicationAttributes?.call_taker_mode_settings;
  const hasCallTakerModeSettings = Boolean(callTakerModeSettings);
  const isCallTakerModeEnabled = Boolean(callTakerModeSettings?.is_enabled);
  const workstationPosition = callTakerModeSettings?.workstation_position || '';
  const rememberWorkstationPosition = Boolean(
    callTakerModeSettings?.remember_workstation_position,
  );
  const autoSwitchToNextCall = Boolean(
    callTakerModeSettings?.auto_switch_to_next_call,
  );
  const hasCTMFormAutoDisplayCapability = Boolean(
    activeCapabilities['call_taker_mode_form_auto_display_0'],
  );

  const resetCTMClick = () => {
    if (isCTMClicked) {
      setIsCTMClicked(false);
    }
  };

  const closeInitiationModal = () => {
    setIsInitiationModalOpen(false);
    resetCTMClick();
  };

  const closeErrorModal = () => {
    setIsErrorModalOpen(false);
    resetCTMClick();
  };

  const closePositionIDSelectionModal = () => {
    setIsPositionIDSelectionModalOpen(false);
    resetCTMClick();
  };

  const openPositionModalOrError = useCallback(() => {
    const hasWorkstationPositions = workstation_positions.length > 0;
    setIsPositionIDSelectionModalOpen(hasWorkstationPositions);
    setIsErrorModalOpen(!hasWorkstationPositions);
  }, [workstation_positions.length]);

  const handleTurnOnClick = () => {
    openPositionModalOrError();
  };

  const disableCTMAndDisplayForm = useCallback(async () => {
    // Auto-display should reset any stale CTM session before the user confirms a seat.
    const data = {
      call_taker_mode_settings: {
        auto_switch_to_next_call: autoSwitchToNextCall,
        is_enabled: false,
        remember_workstation_position: rememberWorkstationPosition,
        workstation_position: workstationPosition,
      },
    };

    try {
      await dispatch(updateUserAttributes(applicationName, data));
      toggleSetCallFilter('seatPosition', false);
      trackCTMEnabled(false, workstationPosition || null, irpVersion);
      openPositionModalOrError();
    } catch (error) {
      notifyError(ctmDisableErrorMsg);
      captureException(error);
    }
  }, [
    applicationName,
    autoSwitchToNextCall,
    dispatch,
    irpVersion,
    openPositionModalOrError,
    rememberWorkstationPosition,
    toggleSetCallFilter,
    workstationPosition,
  ]);

  const handleInitialAutoDisplay = useCallback(() => {
    if (!hasCTMFormAutoDisplayCapability) {
      if (!hasCallTakerModeSettings) {
        setIsInitiationModalOpen(true);
      }
      return;
    }

    if (!hasCallTakerModeSettings) {
      setIsInitiationModalOpen(true);
      return;
    }

    if (isCallTakerModeEnabled) {
      disableCTMAndDisplayForm();
      return;
    }

    openPositionModalOrError();
  }, [
    disableCTMAndDisplayForm,
    hasCTMFormAutoDisplayCapability,
    hasCallTakerModeSettings,
    isCallTakerModeEnabled,
    openPositionModalOrError,
  ]);

  useEffect(() => {
    if (!ctmPermission || hasHandledInitialAutoDisplayRef.current) {
      return;
    }

    hasHandledInitialAutoDisplayRef.current = true;
    handleInitialAutoDisplay();
  }, [ctmPermission, handleInitialAutoDisplay]);

  useEffect(() => {
    if (!isCTMClicked) {
      return;
    }

    // Manual CTM clicks only open the modal when CTM is currently off.
    if (hasCallTakerModeSettings && !isCallTakerModeEnabled) {
      openPositionModalOrError();
      return;
    }

    setIsPositionIDSelectionModalOpen(false);
    setIsErrorModalOpen(false);
  }, [
    hasCallTakerModeSettings,
    isCTMClicked,
    isCallTakerModeEnabled,
    openPositionModalOrError,
  ]);

  return (
    <>
      {isInitiationModalOpen && (
        <InitiationModal
          closeInitiationModal={closeInitiationModal}
          isOpened={isInitiationModalOpen}
          handleTurnOnClick={handleTurnOnClick}
        />
      )}

      {isPositionIDSelectionModalOpen && (
        <PositionIDSelectionModal
          togglePositionIDSelectionModal={closePositionIDSelectionModal}
          isOpened={isPositionIDSelectionModalOpen}
          positionIDs={workstation_positions}
        />
      )}

      {isErrorModalOpen && (
        <ErrorModal
          isOpened={isErrorModalOpen}
          toggleErrorModal={closeErrorModal}
        />
      )}
    </>
  );
};

const CallTakerModalsContainer = memo(
  CallTakerModalsContainerComponent,
  arePropsEqual,
);

export default CallTakerModalsContainer;
